import React, { useState } from 'react';
import {
    Modal,
    Row,
    Col,
    Typography,
    Tag,
    Space,
    Button,
    Divider,
    List,
    Avatar,
    Tooltip,
    message,
    Popconfirm,
    Empty,
    theme
} from 'antd';
import {
    EditOutlined,
    DeleteOutlined,
    UserOutlined,
    SendOutlined,
    CommentOutlined,
    ThunderboltOutlined,
    CloseOutlined,
    SaveOutlined,
    CompassOutlined,
    AimOutlined,
    FileTextOutlined
} from '@ant-design/icons';
import 'react-quill/dist/quill.snow.css';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { strategyApi } from '../../api/client';
import RichMentionEditor from '../../components/common/RichMentionEditor';
import MentionDisplay from '../../components/common/MentionDisplay';
import { useNotificationRefresh } from '../../hooks/useNotificationRefresh';

dayjs.extend(relativeTime);

const { Title, Text } = Typography;

interface StrategicGoal {
    id: string;
    name: string;
    description?: string;
    reason?: string;
    keyResults?: string;
    year: number;
    status: string;
    initiatives?: any[];
    owner?: { id: string; firstName: string; lastName: string };
}

interface GoalComment {
    id: string;
    content: string;
    createdAt: string;
    author: { id: string; firstName: string; lastName: string };
    mentionedUserIds?: string[];
}

interface GoalDetailModalProps {
    goal: StrategicGoal | null;
    open: boolean;
    onClose: () => void;
    onEdit: (goal: StrategicGoal) => void;
    onDelete: (id: string) => void;
    onInitiativeClick: (initiative: any) => void;
}

const GoalDetailModal: React.FC<GoalDetailModalProps> = ({
    goal,
    open,
    onClose,
    onEdit,
    onDelete,
    onInitiativeClick,
}) => {
    const queryClient = useQueryClient();

    const refreshNotifications = useNotificationRefresh();
    const { token } = theme.useToken();

    // Comment state
    const [commentText, setCommentText] = useState('');
    const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
    const [editingCommentContent, setEditingCommentContent] = useState('');

    // Fetch comments independently ensuring freshness
    const { data: comments = [], isLoading: commentsLoading } = useQuery<GoalComment[]>({
        queryKey: ['strategy-goal-comments', goal?.id],
        queryFn: () => goal ? strategyApi.getGoalComments(goal.id).then((res: any) => res.data) : Promise.resolve([]),
        enabled: !!goal && open, // Only fetch when modal is open and goal exists
    });

    const createCommentMutation = useMutation({
        mutationFn: ({ goalId, content, mentionedUserIds }: { goalId: string; content: string; mentionedUserIds?: string[] }) =>
            strategyApi.createGoalComment(goalId, { content, mentionedUserIds }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['strategy-goal-comments', goal?.id] });
            // Also invalidate goals list to update comment count if displayed there
            queryClient.invalidateQueries({ queryKey: ['strategy-goals'] });
            refreshNotifications();
            message.success('Dodano komentarz');
            setCommentText('');
        },
        onError: () => message.error('Błąd dodawania komentarza'),
    });

    const deleteCommentMutation = useMutation({
        mutationFn: (id: string) => strategyApi.deleteGoalComment(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['strategy-goal-comments', goal?.id] });
            queryClient.invalidateQueries({ queryKey: ['strategy-goals'] });
            refreshNotifications();
            message.success('Usunięto komentarz');
        }
    });

    const handleSendComment = () => {
        if (!commentText.trim() || !goal) return;

        // Extract mentioned user IDs
        const mentionedUserIds: string[] = [];
        const regex = /@([^\[]+)\[([^\]]+)\]/g;
        let match;
        while ((match = regex.exec(commentText)) !== null) {
            mentionedUserIds.push(match[2]);
        }

        createCommentMutation.mutate({
            goalId: goal.id,
            content: commentText,
            mentionedUserIds: mentionedUserIds.length > 0 ? mentionedUserIds : undefined
        });
    };

    if (!goal) return null;

    return (
        <Modal
            open={open}
            onCancel={onClose}
            footer={null}
            width={1000}
            centered
            className="goal-detail-modal"
            style={{ top: 20 }}
        >
            <div style={{ padding: '0 8px' }}>
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, paddingRight: 40 }}>
                    <div style={{ flex: 1 }}>
                        <Space align="center" style={{ marginBottom: 8 }}>
                            <Tag color="geekblue">{goal.year}</Tag>
                            <Tag color={goal.status === 'ACTIVE' ? 'green' : 'default'}>{goal.status}</Tag>
                            {goal.owner && (
                                <Tooltip title="Właściciel">
                                    <Avatar size="small" icon={<UserOutlined />} style={{ backgroundColor: '#87d068' }} />
                                    <Text type="secondary" style={{ fontSize: 12, marginLeft: 4 }}>
                                        {goal.owner.firstName} {goal.owner.lastName}
                                    </Text>
                                </Tooltip>
                            )}
                        </Space>
                        <Title level={3} style={{ marginTop: 0, marginBottom: 0 }}>{goal.name}</Title>
                    </div>

                    <Space>
                        <Button icon={<EditOutlined />} onClick={() => onEdit(goal)}>Edytuj</Button>
                        <Popconfirm
                            title="Czy na pewno usunąć ten cel?"
                            onConfirm={() => onDelete(goal.id)}
                            okText="Tak"
                            cancelText="Nie"
                        >
                            <Button danger icon={<DeleteOutlined />}>Usuń</Button>
                        </Popconfirm>
                    </Space>
                </div>

                <Row gutter={32}>
                    {/* LEFT COLUMN - CONTENT */}
                    <Col span={16} style={{ borderRight: `1px solid ${token.colorBorderSecondary}` }}>
                        <Space direction="vertical" size="large" style={{ width: '100%' }}>

                            {/* Reason */}
                            <div style={{ background: token.colorFillAlter, borderRadius: 8, padding: '16px 20px', border: `1px solid ${token.colorBorderSecondary}` }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, color: token.colorPrimary }}>
                                    <CompassOutlined style={{ fontSize: 18 }} />
                                    <Text strong style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        Uzasadnienie biznesowe
                                    </Text>
                                </div>
                                {goal.reason ? (
                                    <div className="ql-editor" style={{ padding: 0 }} dangerouslySetInnerHTML={{ __html: goal.reason }} />
                                ) : (
                                    <Text type="secondary" italic>Brak uzasadnienia</Text>
                                )}
                            </div>

                            {/* Key Results */}
                            <div style={{ background: token.colorFillAlter, borderRadius: 8, padding: '16px 20px', border: `1px solid ${token.colorBorderSecondary}` }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, color: token.colorError }}>
                                    <AimOutlined style={{ fontSize: 18 }} />
                                    <Text strong style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        Kluczowe rezultaty (KR)
                                    </Text>
                                </div>
                                {goal.keyResults ? (
                                    <div className="ql-editor" style={{ padding: 0 }} dangerouslySetInnerHTML={{ __html: goal.keyResults }} />
                                ) : (
                                    <Text type="secondary" italic>Brak zdefiniowanych rezultatów</Text>
                                )}
                            </div>

                            {/* Description (Legacy/Optional) */}
                            {goal.description && (
                                <div style={{ background: token.colorWarningBg, borderRadius: 8, padding: '16px 20px', border: `1px solid ${token.colorWarningBorder}` }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, color: token.colorWarning }}>
                                        <FileTextOutlined style={{ fontSize: 18 }} />
                                        <Text strong style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                            Dodatkowy opis
                                        </Text>
                                    </div>
                                    <div className="ql-editor" style={{ padding: 0 }} dangerouslySetInnerHTML={{ __html: goal.description }} />
                                </div>
                            )}

                        </Space>
                    </Col>

                    {/* RIGHT COLUMN - SIDEBAR */}
                    <Col span={8}>

                        {/* Initiatives List */}
                        <div style={{ marginBottom: 32 }}>
                            <Text strong style={{ display: 'block', marginBottom: 12 }}>
                                <ThunderboltOutlined /> PODPIĘTE INICJATYWY
                            </Text>
                            {goal.initiatives && goal.initiatives.length > 0 ? (
                                <List
                                    size="small"
                                    dataSource={goal.initiatives}
                                    renderItem={(item: any) => (
                                        <List.Item
                                            style={{ padding: '8px 0', cursor: 'pointer' }}
                                            onClick={() => onInitiativeClick(item)}
                                            className="initiative-list-item"
                                        >
                                            <Space>
                                                <Tag color={item.priority === 'P0' ? 'red' : item.priority === 'P1' ? 'orange' : 'blue'}>{item.priority}</Tag>
                                                <Text ellipsis style={{ maxWidth: 150 }}>{item.name}</Text>
                                            </Space>
                                        </List.Item>
                                    )}
                                />
                            ) : (
                                <Text type="secondary">Brak inicjatyw</Text>
                            )}
                        </div>

                        {/* Comments Sidebar - Matched style with InitiativeDetailTabs */}
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            background: token.colorFillQuaternary,
                            borderRadius: 8,
                            padding: 12,
                            maxHeight: 500, // Fixed height for scroll
                        }}>
                            {/* Header */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                                <CommentOutlined style={{ fontSize: 14 }} />
                                <Text strong>Komentarze</Text>
                                <Tag style={{ marginLeft: 'auto' }}>{comments.length}</Tag>
                            </div>

                            {/* Comment Input */}
                            <div style={{ marginBottom: 12 }}>
                                <RichMentionEditor
                                    value={commentText}
                                    onChange={setCommentText}
                                    placeholder="Napisz komentarz... (@mention)"
                                    minRows={2}
                                    onPressEnter={handleSendComment}
                                />
                                <Button
                                    type="primary"
                                    size="small"
                                    icon={<SendOutlined />}
                                    onClick={handleSendComment}
                                    loading={createCommentMutation.isPending}
                                    disabled={!commentText.trim()}
                                    style={{ marginTop: 8 }}
                                >
                                    Wyślij
                                </Button>
                            </div>

                            <Divider style={{ margin: '8px 0' }} />

                            {/* Comments List */}
                            <div style={{
                                flex: 1,
                                overflowY: 'auto',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 8,
                                minHeight: 200
                            }}>
                                {commentsLoading ? (
                                    <div style={{ textAlign: 'center', padding: 20 }}>Ładowanie...</div>
                                ) : comments.length === 0 ? (
                                    <Empty
                                        description={<Text type="secondary">Brak komentarzy</Text>}
                                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                                        style={{ margin: 'auto' }}
                                    />
                                ) : (
                                    comments.map(comment => {
                                        const isEditing = editingCommentId === comment.id;

                                        return (
                                            <div
                                                key={comment.id}
                                                style={{
                                                    display: 'flex',
                                                    gap: 8,
                                                    background: token.colorBgContainer,
                                                    padding: 10,
                                                    borderRadius: 6,
                                                    boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                                                }}
                                            >
                                                <Avatar size="small" style={{ backgroundColor: token.colorPrimary, flexShrink: 0 }}>
                                                    {comment.author.firstName[0]}
                                                </Avatar>
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                                                        <div>
                                                            <Text strong style={{ fontSize: 12 }}>
                                                                {comment.author.firstName} {comment.author.lastName}
                                                            </Text>
                                                            <br />
                                                            <Text type="secondary" style={{ fontSize: 11 }}>
                                                                {dayjs(comment.createdAt).fromNow()}
                                                            </Text>
                                                        </div>
                                                        {!isEditing && (
                                                            <Space size={0}>
                                                                <Popconfirm
                                                                    title="Usunąć komentarz?"
                                                                    onConfirm={() => deleteCommentMutation.mutate(comment.id)}
                                                                >
                                                                    <Button size="small" type="text" danger icon={<DeleteOutlined style={{ fontSize: 11 }} />} style={{ padding: '0 4px', height: 20 }} />
                                                                </Popconfirm>
                                                            </Space>
                                                        )}
                                                    </div>

                                                    {/* Rendering content */}
                                                    <div style={{ fontSize: 13 }}>
                                                        <MentionDisplay content={comment.content} />
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </div>

                    </Col>
                </Row>
            </div>
        </Modal>
    );
};

export default GoalDetailModal;
